<template>
	<section
		class="settings-section general-information-section new-settings__section"
		aria-labelledby="general-information-section-title"
	>
		<header class="new-settings__section-header">
			<div class="new-settings__section-header-row">
				<div class="new-settings__section-header-text">
					<div class="new-settings__section-title-row">
						<h1
							id="general-information-section-title"
							class="new-settings__section-title"
						>
							{{ strings.title }}
						</h1>
						<StatusBadge
							v-if="isPartnerFormReady"
							:status="callerIdVerificationStatus"
						/>
					</div>
					<p class="new-settings__section-subtitle">
						{{ strings.subtitle }}
					</p>
				</div>
			</div>
		</header>

		<!-- <div
			class="settings-section__card settings-section__card--organization"
		>
			<div class="settings-section__card-body">
				<h2 class="settings-section__card-title">
					Organization
				</h2>
				<div class="settings-section__org-row">
					<div class="settings-section__org-primary">
						<span class="settings-section__org-name">
							{{ displayOrganizationName }}
						</span>
						<span
							v-if="showVerifiedBadge"
							class="settings-section__verified-badge"
						>
							Verified
						</span>
					</div>
					<button
						type="button"
						class="settings-section__link-btn"
						@click="onManageOrganization"
					>
						Manage Organization
					</button>
				</div>
			</div>
		</div> -->

		<div class="settings-section__content">
			<engage-loader v-if="isPartnerFormLoading" overlay />

			<form class="settings-section__grid" @submit.prevent>
				<div class="settings-section__card-wrap">
					<div class="settings-section__card">
						<div class="settings-section__card-header">
							<h2 class="settings-section__card-title">
								{{ strings.practiceCardTitle }}
							</h2>
							<div
								v-if="!isPartnerFormLoading"
								class="settings-section__card-actions"
							>
								<button
									v-if="!panelEditing.practice"
									type="button"
									class="btn-pill btn-pill--sm"
									@click="startPanelEditing('practice')"
								>
									{{ common.edit }}
								</button>
								<div v-else class="btn-pill-group">
									<button
										type="button"
										class="btn-pill btn-pill--sm"
										@click="cancelPanelEditing('practice')"
									>
										{{ common.cancel }}
									</button>

									<button
										type="button"
										class="btn-pill btn-pill--sm"
										:disabled="
											isPanelSaveDisabled('practice')
										"
										@click="onPanelSave('practice')"
									>
										{{ common.save }}
									</button>
								</div>
							</div>
						</div>
						<div class="settings-section__card-body">
							<div class="settings-section__field-row">
								<div class="settings-section__field">
									<label
										for="gi-name"
										class="settings-section__label"
										:class="{
											'settings-section__label--error': hasCallerIdVerificationFieldError(
												'name'
											),
										}"
									>
										{{ strings.fields.practiceName }}
									</label>
									<div
										class="settings-section__readonly settings-section__readonly--highlight"
										:class="{
											'settings-section__readonly--error': hasCallerIdVerificationFieldError(
												'name'
											),
										}"
									>
										{{ form.name }}
									</div>
								</div>

								<div class="settings-section__field">
									<label
										for="gi-wma_display_name"
										class="settings-section__label"
									>
										{{ strings.fields.webDisplayName }}
									</label>
									<div
										class="settings-section__readonly settings-section__readonly--highlight"
									>
										{{ form.wma_display_name || form.name }}
									</div>
								</div>
							</div>

							<div
								v-if="isWMAEnabledPartner"
								class="settings-section__field"
							>
								<label
									for="gi-wma_web_link"
									class="settings-section__label"
									:class="{
										'settings-section__label--error': hasCallerIdVerificationFieldError(
											'wma_web_link'
										),
									}"
								>
									{{ strings.fields.wmaWeblink }}
								</label>
								<div
									class="settings-section__readonly settings-section__readonly--highlight settings-section__readonly-with-action"
									:class="{
										'settings-section__readonly--error': hasCallerIdVerificationFieldError(
											'wma_web_link'
										),
									}"
								>
									<a
										v-if="form.wma_web_link"
										:href="form.wma_web_link"
										target="_blank"
										rel="noopener noreferrer"
										class="settings-section__readonly-link"
									>
										{{ form.wma_web_link }}
									</a>
									<button
										v-if="form.wma_web_link"
										type="button"
										class="settings-section__icon-btn settings-section__readonly-action"
										:data-tooltip="
											strings.tooltips.copyWmaWeblink
										"
										data-position="left"
										@click.prevent="onCopyWMALink"
									>
										<font-awesome-icon
											:icon="['far', 'clone']"
											:rotation="90"
										/>
									</button>
								</div>
							</div>

							<div
								v-if="forwardingNumbersRows.length <= 1"
								class="settings-section__field settings-section__field--forwarding-table"
							>
								<label class="settings-section__label">{{
									strings.fields.forwardingNumber
								}}</label>
								<div
									class="settings-section__fn-table-wrap settings-section__fn-table-wrap--compact"
								>
									<engage-loader
										v-if="isForwardingNumbersRefreshing"
										overlay
									/>
									<div
										class="settings-section__readonly settings-section__readonly--highlight"
									>
										<template
											v-if="
												forwardingNumbersRows.length > 0
											"
										>
											{{
												forwardingNumbersRows[0]
													.phone_number
													| newFormatPhoneNumber
											}}
										</template>
									</div>
								</div>
							</div>

							<div
								v-if="
									forwardingNumbersRows.length > 1 &&
										partner &&
										partner.twilio_phone_number &&
										partner.twilio_phone_number.trim() !==
											''
								"
								class="settings-section__field settings-section__field--forwarding-table"
							>
								<label class="settings-section__label">{{
									strings.fields.forwardingNumber
								}}</label>
								<div class="settings-section__fn-table-wrap">
									<engage-loader
										v-if="isForwardingNumbersRefreshing"
										overlay
									/>
									<table class="settings-section__fn-table">
										<thead>
											<tr>
												<th scope="col">
													{{
														strings.fields
															.forwardingNumber
													}}
												</th>
												<th scope="col">
													{{
														strings.table
															.ivrAssignment
													}}
												</th>
												<th scope="col">
													{{
														strings.table
															.selectSingleForwardingNumber
													}}
												</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="row in forwardingNumbersRows"
												:key="row.id"
											>
												<td>
													{{
														row.phone_number
															| newFormatPhoneNumber
													}}
												</td>
												<td
													v-if="
														selectedForwardingNumber ===
															null
													"
												>
													{{
														assignedOfficeHours(
															row.assigned_office_hours
														)
													}}
												</td>
												<td
													v-if="
														selectedForwardingNumber ===
															row.id &&
															selectedForwardingNumber !==
																null
													"
												>
													{{ ivrAssignmentRowText }}
												</td>
												<td
													v-if="
														selectedForwardingNumber !==
															row.id &&
															selectedForwardingNumber !==
																null
													"
												>
													{{ '-' }}
												</td>
												<td>
													<button
														v-if="
															selectedForwardingNumber ===
																null &&
																isAssignedOfficeHourContainsClosed(
																	row.assigned_office_hours
																)
														"
														type="button"
														class="settings-section__fn-select"
														@click.prevent.stop="
															selectForwardingNumber(
																row.id
															)
														"
													>
														{{ common.select }}
													</button>
													<div
														v-if="
															selectedForwardingNumber ===
																row.id &&
																selectedForwardingNumber !==
																	null
														"
														class="settings-section__toolbar"
													>
														<button
															type="button"
															class="settings-section__fn-select"
															@click.prevent.stop="
																onConfirmForwardingNumber
															"
														>
															{{ common.confirm }}
														</button>
														<button
															type="button"
															class="settings-section__link-btn"
															@click.prevent.stop="
																onCancelForwardingNumber
															"
														>
															{{ common.cancel }}
														</button>
													</div>
													<span
														v-if="
															selectedForwardingNumber !==
																row.id &&
																selectedForwardingNumber !==
																	null
														"
													>
														{{
															strings.forwardingNumberWillBeRemoved
														}}
													</span>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>

							<div
								class="settings-section__field settings-section__field--outbound-caller-id"
							>
								<label
									for="gi-outbound_caller_id"
									class="settings-section__label"
									:class="{
										'settings-section__label--error': hasCallerIdVerificationFieldError(
											'ob_caller_id_number'
										),
									}"
								>
									{{ strings.fields.outboundCallerId }}
								</label>
								<div class="settings-section__input-action-row">
									<div
										class="settings-section__input-clear-wrap settings-section__input-action-row__input"
									>
										<input
											id="gi-outbound_caller_id"
											v-model="rawOutboundCallerIdNumber"
											type="text"
											class="settings-section__input"
											:class="{
												'settings-section__input--clearable':
													panelEditing.practice,
												'settings-section__input--error': hasCallerIdVerificationFieldError(
													'ob_caller_id_number'
												),
											}"
											:placeholder="
												placeholders.phoneNumber
											"
											:readonly="!panelEditing.practice"
											:aria-invalid="
												hasCallerIdVerificationFieldError(
													'ob_caller_id_number'
												)
											"
											@input="
												validateOutboundCallerIdNumber
											"
										/>
										<button
											v-if="
												panelEditing.practice &&
													form.ob_caller_id_number
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="
												aria.clearOutboundCallerId
											"
											@click="clearOutboundCallerIdNumber"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
									<button
										v-if="isOutboundNumberVerified"
										type="button"
										class="settings-section__btn-green"
									>
										{{ common.verified }}
									</button>
									<button
										v-else
										type="button"
										class="settings-section__btn-purple"
										:disabled="isVerifyCallerIdDisabled"
										@click="openVerifyCallerIdPanel"
									>
										{{ strings.callerId.verifyButton }}
									</button>
								</div>
								<p class="settings-section__help-text">
									{{ strings.callerId.helpText }}
								</p>
							</div>

							<div class="settings-section__field-row">
								<div class="settings-section__field">
									<label
										for="gi-office_phone_number"
										class="settings-section__label"
										:class="{
											'settings-section__label--error': hasCallerIdVerificationFieldError(
												'office_phone_number'
											),
										}"
									>
										{{ strings.fields.officePhoneNumber }}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="gi-office_phone_number"
											v-model="rawOfficePhoneNumber"
											type="text"
											class="settings-section__input"
											:class="{
												'settings-section__input--clearable':
													panelEditing.practice,
												'settings-section__input--error': hasCallerIdVerificationFieldError(
													'office_phone_number'
												),
											}"
											:placeholder="
												placeholders.phoneNumber
											"
											:readonly="!panelEditing.practice"
											:aria-invalid="
												hasCallerIdVerificationFieldError(
													'office_phone_number'
												)
											"
											@input="validateOfficePhoneNumber"
										/>
										<button
											v-if="
												panelEditing.practice &&
													rawOfficePhoneNumber
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="
												aria.clearOfficePhoneNumber
											"
											@click="
												clearGeneralInfoField(
													'office_phone_number'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>
								<div class="settings-section__field">
									<label
										for="gi-office_fax_number"
										class="settings-section__label"
										:class="{
											'settings-section__label--error': hasCallerIdVerificationFieldError(
												'office_fax_number'
											),
										}"
									>
										{{ strings.fields.officeFaxNumber }}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="gi-office_fax_number"
											v-model="rawOfficeFaxNumber"
											type="text"
											class="settings-section__input"
											:class="{
												'settings-section__input--clearable':
													panelEditing.practice,
												'settings-section__input--error': hasCallerIdVerificationFieldError(
													'office_fax_number'
												),
											}"
											:placeholder="
												placeholders.faxNumber
											"
											:readonly="!panelEditing.practice"
											:aria-invalid="
												hasCallerIdVerificationFieldError(
													'office_fax_number'
												)
											"
											@input="validateOfficeFaxNumber"
										/>
										<button
											v-if="
												panelEditing.practice &&
													rawOfficeFaxNumber
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="
												aria.clearOfficeFaxNumber
											"
											@click="
												clearGeneralInfoField(
													'office_fax_number'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>
							</div>

							<div class="settings-section__field">
								<label
									for="gi-timezone"
									class="settings-section__label"
								>
									{{ strings.fields.timezone }}
								</label>
								<div class="settings-section__select-wrap">
									<select
										id="gi-timezone"
										v-model="form.timezone"
										class="settings-section__select"
										:class="{
											'settings-section__select--readonly': !panelEditing.practice,
										}"
										:tabindex="
											panelEditing.practice ? 0 : -1
										"
									>
										<option :value="null" disabled>
											{{ strings.selectTimezone }}
										</option>
										<option
											v-for="timezone in timezones"
											:key="timezone"
											:value="timezone"
										>
											{{ timezone }}
										</option>
									</select>
									<font-awesome-icon
										class="settings-section__select-icon"
										:icon="['far', 'chevron-down']"
										aria-hidden="true"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="settings-section__grid-stack">
					<div class="settings-section__card-wrap">
						<div class="settings-section__card">
							<div class="settings-section__card-header">
								<h2 class="settings-section__card-title">
									{{ strings.addressCardTitle }}
								</h2>
								<div
									v-if="!isPartnerFormLoading"
									class="settings-section__card-actions"
								>
									<button
										v-if="!panelEditing.address"
										type="button"
										class="btn-pill btn-pill--sm"
										@click="startPanelEditing('address')"
									>
										{{ common.edit }}
									</button>
									<div v-else class="btn-pill-group">
										<button
											type="button"
											class="btn-pill btn-pill--sm"
											@click="
												cancelPanelEditing('address')
											"
										>
											{{ common.cancel }}
										</button>

										<button
											type="button"
											class="btn-pill btn-pill--sm"
											:disabled="
												isPanelSaveDisabled('address')
											"
											@click="onPanelSave('address')"
										>
											{{ common.save }}
										</button>
									</div>
								</div>
							</div>
							<div class="settings-section__card-body">
								<div class="settings-section__field">
									<label
										for="gi-address_1"
										class="settings-section__label"
										:class="{
											'settings-section__label--error': hasCallerIdVerificationFieldError(
												'address_1'
											),
										}"
									>
										{{ strings.fields.addressLine1 }}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="gi-address_1"
											v-model="form.address_1"
											type="text"
											class="settings-section__input"
											:class="{
												'settings-section__input--clearable':
													panelEditing.address,
												'settings-section__input--error': hasCallerIdVerificationFieldError(
													'address_1'
												),
											}"
											:placeholder="
												placeholders.addressLine1
											"
											:readonly="!panelEditing.address"
											:aria-invalid="
												hasCallerIdVerificationFieldError(
													'address_1'
												)
											"
										/>
										<button
											v-if="
												panelEditing.address &&
													form.address_1
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="aria.clearAddressLine1"
											@click="
												clearGeneralInfoField(
													'address_1'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>
								<div class="settings-section__field">
									<label
										for="gi-address_2"
										class="settings-section__label"
									>
										{{
											strings.fields.addressLine2Optional
										}}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="gi-address_2"
											v-model="form.address_2"
											type="text"
											class="settings-section__input"
											:class="{
												'settings-section__input--clearable':
													panelEditing.address,
											}"
											:placeholder="
												placeholders.addressLine2
											"
											:readonly="!panelEditing.address"
										/>
										<button
											v-if="
												panelEditing.address &&
													form.address_2
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="aria.clearAddressLine2"
											@click="
												clearGeneralInfoField(
													'address_2'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>
								<div class="settings-section__field-row">
									<div class="settings-section__field">
										<label
											for="gi-city"
											class="settings-section__label"
											:class="{
												'settings-section__label--error': hasCallerIdVerificationFieldError(
													'city'
												),
											}"
											>{{ strings.fields.city }}</label
										>
										<div
											class="settings-section__input-clear-wrap"
										>
											<input
												id="gi-city"
												v-model="form.city"
												type="text"
												class="settings-section__input"
												:class="{
													'settings-section__input--clearable':
														panelEditing.address,
													'settings-section__input--error': hasCallerIdVerificationFieldError(
														'city'
													),
												}"
												:placeholder="placeholders.city"
												:readonly="
													!panelEditing.address
												"
												:aria-invalid="
													hasCallerIdVerificationFieldError(
														'city'
													)
												"
											/>
											<button
												v-if="
													panelEditing.address &&
														form.city
												"
												type="button"
												class="settings-section__input-clear"
												:aria-label="aria.clearCity"
												@click="
													clearGeneralInfoField(
														'city'
													)
												"
											>
												<font-awesome-icon
													:icon="['fal', 'times']"
												/>
											</button>
										</div>
									</div>
									<div class="settings-section__field">
										<label
											for="gi-state_province"
											class="settings-section__label"
											:class="{
												'settings-section__label--error': hasCallerIdVerificationFieldError(
													'state_province'
												),
											}"
										>
											{{ strings.fields.stateProvince }}
										</label>
										<div
											class="settings-section__input-clear-wrap"
										>
											<input
												id="gi-state_province"
												v-model="form.state_province"
												type="text"
												class="settings-section__input"
												:class="{
													'settings-section__input--clearable':
														panelEditing.address,
													'settings-section__input--error': hasCallerIdVerificationFieldError(
														'state_province'
													),
												}"
												:placeholder="
													placeholders.stateOrProvince
												"
												:readonly="
													!panelEditing.address
												"
												:aria-invalid="
													hasCallerIdVerificationFieldError(
														'state_province'
													)
												"
											/>
											<button
												v-if="
													panelEditing.address &&
														form.state_province
												"
												type="button"
												class="settings-section__input-clear"
												:aria-label="
													aria.clearStateOrProvince
												"
												@click="
													clearGeneralInfoField(
														'state_province'
													)
												"
											>
												<font-awesome-icon
													:icon="['fal', 'times']"
												/>
											</button>
										</div>
									</div>
								</div>
								<div class="settings-section__field-row">
									<div class="settings-section__field">
										<label
											for="gi-country"
											class="settings-section__label"
											:class="{
												'settings-section__label--error': hasCallerIdVerificationFieldError(
													'country'
												),
											}"
										>
											{{ strings.fields.country }}
										</label>
										<div
											class="settings-section__input-clear-wrap"
										>
											<input
												id="gi-country"
												v-model="form.country"
												type="text"
												class="settings-section__input"
												:class="{
													'settings-section__input--clearable':
														panelEditing.address,
													'settings-section__input--error': hasCallerIdVerificationFieldError(
														'country'
													),
												}"
												:placeholder="
													placeholders.country
												"
												:readonly="
													!panelEditing.address
												"
												:aria-invalid="
													hasCallerIdVerificationFieldError(
														'country'
													)
												"
											/>
											<button
												v-if="
													panelEditing.address &&
														form.country
												"
												type="button"
												class="settings-section__input-clear"
												:aria-label="aria.clearCountry"
												@click="
													clearGeneralInfoField(
														'country'
													)
												"
											>
												<font-awesome-icon
													:icon="['fal', 'times']"
												/>
											</button>
										</div>
									</div>
									<div class="settings-section__field">
										<label
											for="gi-postal_code"
											class="settings-section__label"
											:class="{
												'settings-section__label--error': hasCallerIdVerificationFieldError(
													'postal_code'
												),
											}"
										>
											{{ strings.fields.postalCode }}
										</label>
										<div
											class="settings-section__input-clear-wrap"
										>
											<input
												id="gi-postal_code"
												v-model="form.postal_code"
												type="text"
												class="settings-section__input"
												:class="{
													'settings-section__input--clearable':
														panelEditing.address,
													'settings-section__input--error': hasCallerIdVerificationFieldError(
														'postal_code'
													),
												}"
												autocomplete="postal-code"
												:placeholder="
													placeholders.postalCode
												"
												:readonly="
													!panelEditing.address
												"
												:aria-invalid="
													hasCallerIdVerificationFieldError(
														'postal_code'
													)
												"
											/>
											<button
												v-if="
													panelEditing.address &&
														form.postal_code
												"
												type="button"
												class="settings-section__input-clear"
												:aria-label="
													aria.clearPostalCode
												"
												@click="
													clearGeneralInfoField(
														'postal_code'
													)
												"
											>
												<font-awesome-icon
													:icon="['fal', 'times']"
												/>
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="settings-section__card-wrap">
						<div class="settings-section__card">
							<div class="settings-section__card-header">
								<h2 class="settings-section__card-title">
									{{ strings.fallbackCardTitle }}
								</h2>
								<div
									v-if="!isPartnerFormLoading"
									class="settings-section__card-actions"
								>
									<button
										v-if="!panelEditing.fallback"
										type="button"
										class="btn-pill btn-pill--sm"
										@click="startPanelEditing('fallback')"
									>
										{{ common.edit }}
									</button>
									<div v-else class="btn-pill-group">
										<button
											type="button"
											class="btn-pill btn-pill--sm"
											@click="
												cancelPanelEditing('fallback')
											"
										>
											{{ common.cancel }}
										</button>

										<button
											type="button"
											class="btn-pill btn-pill--sm"
											:disabled="
												isPanelSaveDisabled('fallback')
											"
											@click="onPanelSave('fallback')"
										>
											{{ common.save }}
										</button>
									</div>
								</div>
							</div>
							<div class="settings-section__card-body">
								<div class="settings-section__field">
									<label
										for="gi-fallback_email"
										class="settings-section__label"
									>
										{{
											strings.fields
												.fallbackNotificationEmail
										}}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="gi-fallback_email"
											v-model="form.fallback_email"
											type="text"
											class="settings-section__input"
											:class="{
												'settings-section__input--clearable':
													panelEditing.fallback,
											}"
											:placeholder="
												placeholders.fallbackNotificationEmail
											"
											:readonly="!panelEditing.fallback"
										/>
										<button
											v-if="
												panelEditing.fallback &&
													form.fallback_email
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="
												aria.clearFallbackNotificationEmail
											"
											@click="
												clearGeneralInfoField(
													'fallback_email'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
								</div>
								<div class="settings-section__field">
									<label
										for="gi-fallback_phone_number"
										class="settings-section__label"
									>
										{{
											strings.fields
												.fallbackNotificationPhoneNumber
										}}
									</label>
									<div
										class="settings-section__input-clear-wrap"
									>
										<input
											id="gi-fallback_phone_number"
											v-model="rawFallbackPhoneNumber"
											type="text"
											class="settings-section__input"
											:class="{
												'settings-section__input--clearable':
													panelEditing.fallback,
											}"
											:placeholder="
												placeholders.notificationPhoneNumber
											"
											:readonly="!panelEditing.fallback"
											@input="validateFallbackPhoneNumber"
										/>
										<button
											v-if="
												panelEditing.fallback &&
													rawFallbackPhoneNumber
											"
											type="button"
											class="settings-section__input-clear"
											:aria-label="
												aria.clearFallbackNotificationPhoneNumber
											"
											@click="
												clearGeneralInfoField(
													'fallback_phone_number'
												)
											"
										>
											<font-awesome-icon
												:icon="['fal', 'times']"
											/>
										</button>
									</div>
									<p
										v-if="fallbackNumberErrorMessage"
										class="settings-section__error"
									>
										{{ fallbackNumberErrorMessage }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		</div>

		<general-popup
			:show="showGeneralPopup"
			:message="generalPopupMessage"
			:positive-label="positiveLabel"
			:negative-label="negativeLabel"
			:positive-first="positiveFirst"
			@yes="onGeneralPopupYes"
			@no="onGeneralPopupNo"
		/>

		<verify-caller-id-panel
			:show="showVerifyCallerIdPanel"
			:phone-number="form.ob_caller_id_number"
			@close="closeVerifyCallerIdPanel"
			@phone-number-change="onVerifyPanelPhoneNumberChange"
			@verified="onOutboundCallerIdVerified"
		/>
	</section>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapState } from 'vuex'
import GeneralPopup from '@/components/Panels/GeneralPopup.vue'
import VerifyCallerIdPanel from '@/components/Panels/VerifyCallerIdPanel.vue'
import EngageLoader from '@/components/EngageLoader.vue'
import StatusBadge from '@/components/StatusBadge.vue'
import {
	GENERAL_INFORMATION_STRINGS,
	POP_UP_CONFIRMATION_MESSAGE,
	SETTINGS_ARIA,
	SETTINGS_COMMON,
	SETTINGS_PLACEHOLDERS,
	SETTINGS_VALIDATION,
	WMA_WEBLINK_ERROR_MESSAGE,
	WMA_WEBLINK_SUCCESS_MESSAGE,
} from '@/config/settingsConstants'
import messages from '@/config/messages'
import util from '@/util'

/**
 * Form fields grouped per General Information card panel.
 *
 * @type {Object}
 */
const GENERAL_INFO_PANEL_FIELDS = {
	practice: [
		'office_phone_number',
		'office_fax_number',
		'timezone',
		'ob_caller_id_number',
	],
	address: [
		'address_1',
		'address_2',
		'city',
		'state_province',
		'postal_code',
		'country',
	],
	fallback: ['fallback_email', 'fallback_phone_number', 'rsm_fallback_email'],
}

/** Panel ids used for exclusive edit mode. */
const GENERAL_INFO_PANEL_IDS = Object.keys(GENERAL_INFO_PANEL_FIELDS)

/**
 * Partner Information fields required before Caller ID verification.
 *
 * @type {Array<String>}
 */
const CALLER_ID_VERIFICATION_REQUIRED_FIELDS = [
	'name',
	'ob_caller_id_number',
	'office_phone_number',
	'office_fax_number',
	'wma_web_link',
	'address_1',
	'city',
	'state_province',
	'postal_code',
	'country',
]

/**
 * Empty partner settings form shape.
 *
 * @return {Object}
 */
function getSettingsFormObject() {
	return {
		name: '',
		wma_display_name: '',
		wma_web_link: '',
		office_phone_number: '',
		office_fax_number: '',
		ob_caller_id_number: '',
		address_1: '',
		address_2: '',
		city: '',
		state_province: '',
		country: '',
		postal_code: '',
		fallback_email: '',
		fallback_phone_number: '',
		timezone: '',
		rsm_fallback_email: '',
		passcode: '',
		ehr_config: {
			username: '',
			password: '',
			destination_id: '',
			service_application_id: '',
			service_facility_id: '',
			note_types: '',
			metadata: '',
		},
	}
}

/**
 * General information settings — configuration fields from legacy Settings Index.
 */
export default {
	components: {
		GeneralPopup,
		VerifyCallerIdPanel,
		StatusBadge,
		EngageLoader,
	},

	inject: {
		ensureSettingsPartnerLoaded: { default: null },
		selectSettingsSection: { default: null },
	},

	computed: {
		rawOfficeFaxNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(this.form.office_fax_number)
			},
			set(value) {
				this.form.office_fax_number = util.removeNonNumericCharsOMD(
					value
				)
			},
		},

		rawOfficePhoneNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(
					this.form.office_phone_number
				)
			},
			set(value) {
				this.form.office_phone_number = util.removeNonNumericCharsOMD(
					value
				)
			},
		},

		rawFallbackPhoneNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(
					this.form.fallback_phone_number
				)
			},
			set(value) {
				this.form.fallback_phone_number = util.removeNonNumericCharsOMD(
					value
				)
			},
		},

		fallbackEmailTags: {
			get() {
				const emails = this.form.rsm_fallback_email
				if (emails && emails.trim()) {
					return emails.split(/,|;|\s+/).filter(email => email.trim())
				}
				return []
			},
			set(newEmails) {
				this.form.rsm_fallback_email = newEmails.join(', ')
			},
		},

		forwardingNumbersRows() {
			const fromPartner = this.partner?.forwarding_numbers
			if (Array.isArray(fromPartner) && fromPartner.length > 0) {
				return fromPartner.map((item, index) => ({
					id: index + 1,
					...item,
				}))
			}
			return []
		},

		ivrAssignmentRowText() {
			return this.forwardingNumbersRows
				.map(row => this.assignedOfficeHours(row.assigned_office_hours))
				.join(', ')
		},

		generalPopupMessage() {
			if (!this.selectedForwardingNumber) {
				return ''
			}
			const forwardingNumber = this.forwardingNumbersRows.find(
				row => row.id === this.selectedForwardingNumber
			)?.phone_number
			const removedNumbers = this.forwardingNumbersRows.filter(
				row => row.id !== this.selectedForwardingNumber
			)
			return `${POP_UP_CONFIRMATION_MESSAGE.message1}
${util.newFormatPhoneNumberOMD(forwardingNumber)}
${POP_UP_CONFIRMATION_MESSAGE.message2}
${removedNumbers
	.map(row => util.newFormatPhoneNumberOMD(row.phone_number))
	.join('\n')}`
		},

		isSaveDisabled() {
			if (!this.partner) {
				return true
			}
			if (!this.lastSavedSnapshot) {
				return true
			}
			return !this.hasUnsavedChanges
		},

		isPartnerFormReady() {
			return !this.isPartnerFormLoading && Boolean(this.partner?.id)
		},

		hasUnsavedChanges() {
			if (!this.lastSavedSnapshot) {
				return false
			}
			return this.lastSavedSnapshot !== this.getCurrentSnapshot()
		},

		isWMAEnabledPartner() {
			return this.partner?.wma_submit && this.officeOpenInfo.visible
		},

		partnerId() {
			return parseInt(this.$route.params.partner, 10)
		},

		...mapGetters({
			partner: 'partners/active',
		}),

		...mapState('settings', {
			officeOpenInfo: state => state.officeOpenInfo,
		}),

		/**
		 * Organization label shown in the summary card.
		 *
		 * @return {String}
		 */
		displayOrganizationName() {
			return this.organizationName || this.form.name || ''
		},

		showVerifiedBadge() {
			return Boolean(this.partner?.twilio_phone_number?.trim())
		},

		callerIdVerificationStatus() {
			const status =
				this.partner && this.partner.ob_caller_id_verification_status

			return status || 'unverified'
		},

		isOutboundNumberVerified() {
			return (
				this.partner?.ob_caller_id_verification_status?.trim() ===
				'verified'
			)
		},

		rawOutboundCallerIdNumber: {
			get() {
				return util.newFormatPhoneNumberOMD(
					this.form.ob_caller_id_number
				)
			},
			set(value) {
				this.form.ob_caller_id_number = util.removeNonNumericCharsOMD(
					value
				)
			},
		},

		isPartnerInformationCompleteForCallerIdVerification() {
			return CALLER_ID_VERIFICATION_REQUIRED_FIELDS.every(field =>
				this.isPartnerFieldValidForCallerIdVerification(field)
			)
		},

		isOutboundCallerIdValid() {
			const sanitizedPhoneNumber = (
				this.form.ob_caller_id_number || ''
			).replace(/\D/g, '')

			if (!sanitizedPhoneNumber) {
				return false
			}

			return /^[2-9]\d{9}$/.test(sanitizedPhoneNumber)
		},

		isVerifyCallerIdDisabled() {
			return (
				!this.isPartnerInformationCompleteForCallerIdVerification ||
				Boolean(this.activeEditingPanel)
			)
		},

		showCallerIdVerificationRequiredErrors() {
			return !this.isPartnerInformationCompleteForCallerIdVerification
		},

		activeEditingPanel() {
			return (
				GENERAL_INFO_PANEL_IDS.find(
					panel => this.panelEditing[panel]
				) || null
			)
		},
	},

	methods: {
		assignedOfficeHours(data) {
			if (Array.isArray(data) && data.length > 0) {
				return (
					data
						.map(item => util.capitalizeFirstLetter(item))
						.join(', ') || ''
				)
			}
			return data || ''
		},

		isAssignedOfficeHourContainsClosed(data) {
			if (Array.isArray(data) && data.length > 0) {
				return data.some(item => item.toLowerCase().includes('closed'))
			}
			return false
		},

		fields() {
			return Object.keys(this.form)
		},

		fillForm() {
			if (!this.partner) {
				return this.resetFormFields()
			}

			this.fields().forEach(key => {
				if (key === 'ehr_config') {
					return
				}

				if (
					this.partner[key] !== null &&
					typeof this.partner[key] !== 'undefined'
				) {
					if (
						[
							'office_phone_number',
							'office_fax_number',
							'fallback_phone_number',
							'ob_caller_id_number',
						].includes(key)
					) {
						this.form[key] = this.partner[key].replace(/^\+1/, '')
					} else {
						this.form[key] = this.partner[key]
					}
				}
			})

			if (this.partner.ehr_settings) {
				this.form.ehr_config.username =
					this.partner.ehr_settings.username || ''
				this.form.ehr_config.password =
					this.partner.ehr_settings.password || ''
				this.form.ehr_config.destination_id =
					this.partner.ehr_settings.destination_id || ''
				this.form.ehr_config.service_application_id =
					this.partner.ehr_settings.service_application_id || ''
				this.form.ehr_config.service_facility_id =
					this.partner.ehr_settings.service_facility_id || ''
				this.form.ehr_config.metadata =
					this.partner.ehr_settings.metadata || {}
			}

			this.officeNumberErrorMessage = ''
			this.officeFaxNumberErrorMessage = ''
			this.passcodeErrorMessage = ''
			this.fallbackNumberErrorMessage = ''
			this.emailErrorMessage = ''
			this.showPasscode = false
			this.wasCleared = false
			this.form.rsm_fallback_email =
				this.partner.rsm_fallback_email ||
				this.partner.fallback_email ||
				''
		},

		resetFormFields() {
			this.form = getSettingsFormObject()
		},

		getNoteTypesPayloadForSave() {
			const raw = this.partner?.ehr_settings?.note_types
			if (!raw) {
				return []
			}
			try {
				return JSON.parse(raw)
			} catch (e) {
				return []
			}
		},

		findDuplicates(emails) {
			const duplicates = []
			const emailMap = new Map()

			emails.forEach(email => {
				const normalizedEmail = email.toLowerCase()
				if (emailMap.has(normalizedEmail)) {
					duplicates.push(email)
				} else {
					emailMap.set(normalizedEmail, true)
				}
			})
			return duplicates
		},

		async savePartnerSettings() {
			if (this.isSaveDisabled) {
				return false
			}

			if (!this.validatePasscode()) {
				this.$alert.error(this.passcodeErrorMessage)
				return false
			}

			this.validateOfficePhoneNumber()
			this.validateOfficeFaxNumber()

			if (!this.validateFallbackPhoneNumber()) {
				this.$alert.error(this.fallbackNumberErrorMessage)
				return false
			}

			if (!this.form.rsm_fallback_email && this.form.fallback_email) {
				this.form.rsm_fallback_email = this.form.fallback_email
			}

			if (this.form.rsm_fallback_email) {
				const emails = this.form.rsm_fallback_email
					.split(/,|;|\n|\s/)
					.map(email => email.trim())
					.filter(email => email)
				const duplicates = this.findDuplicates(emails)
				if (duplicates.length > 0) {
					this.$alert.error(
						SETTINGS_VALIDATION.duplicateEmails(duplicates)
					)
					return false
				}
				this.form.rsm_fallback_email = emails.join(', ')
			}

			const payload = {
				partner: {
					id: this.partner.id,
					name: this.form.name,
					wma_display_name: this.form.wma_display_name,
					wma_web_link: this.form.wma_web_link,
					office_phone_number: this.form.office_phone_number,
					office_fax_number: this.form.office_fax_number,
					ob_caller_id_number: this.form.ob_caller_id_number,
					address_1: this.form.address_1,
					address_2: this.form.address_2,
					city: this.form.city,
					state_province: this.form.state_province,
					postal_code: this.form.postal_code,
					rsm_fallback_email: this.form.rsm_fallback_email,
					country: this.form.country,
					fallback_email: this.form.fallback_email,
					fallback_phone_number: this.form.fallback_phone_number,
					timezone: this.form.timezone,
					passcode: this.form.passcode,
				},
				ehr_config: {
					username: this.form.ehr_config.username,
					password: this.form.ehr_config.password,
					destination_id: this.form.ehr_config.destination_id,
					service_application_id: this.form.ehr_config
						.service_application_id,
					service_facility_id: this.form.ehr_config
						.service_facility_id,
					note_types: this.getNoteTypesPayloadForSave(),
					metadata: this.form.ehr_config.metadata,
				},
			}

			try {
				await this.update({
					id: this.partner.id,
					fromSettings: true,
					payload,
				})
				this.$alert.success('partners.success')
				this.takeSavedSnapshot()
				this.showPasscode = false
				this.wasCleared = false
				return true
			} catch (e) {
				this.$alert.error('partners.error')
				return false
			}
		},

		selectForwardingNumber(id) {
			this.selectedForwardingNumber = id || null
		},

		onConfirmForwardingNumber() {
			this.showGeneralPopup = true
		},

		onCancelForwardingNumber() {
			this.selectedForwardingNumber = null
		},

		async onGeneralPopupYes() {
			this.showGeneralPopup = false
			try {
				await this.setForwardingNumber({
					id: this.partnerId,
					service_number: this.forwardingNumbersRows.find(
						row => row.id === this.selectedForwardingNumber
					).phone_number,
				})
				this.isForwardingNumbersRefreshing = true
				this.$alert.success(messages.settings.forwardingNumber.success)
				this.selectedForwardingNumber = null
			} catch (error) {
				console.error('Error forwarding number:', error)
				this.$alert.error(messages.settings.forwardingNumber.error)
			} finally {
				this.selectedForwardingNumber = null
				await this.reloadPartner()
			}
		},

		onGeneralPopupNo() {
			this.showGeneralPopup = false
			this.selectedForwardingNumber = null
		},

		async reloadPartner() {
			this.resetFormFields()
			this.fillForm()
			await this.refreshSettingsPartner()
		},

		async initializeSettingsPartnerForm() {
			await this.loadPartnerForm()
		},

		async loadPartnerForm({ force = false } = {}) {
			try {
				this.isPartnerFormLoading = true

				if (this.ensureSettingsPartnerLoaded) {
					await this.ensureSettingsPartnerLoaded(force)
					this.isForwardingNumbersRefreshing = false
					this.fillForm()
					this.takeSavedSnapshot()
					return
				}

				await this.getPartnerData()
			} finally {
				this.isPartnerFormLoading = false
			}
		},

		async refreshSettingsPartner() {
			if (this.ensureSettingsPartnerLoaded) {
				await this.ensureSettingsPartnerLoaded(true)
				this.isForwardingNumbersRefreshing = false
				this.fillForm()
				this.takeSavedSnapshot()
				return
			}

			await this.getPartnerData()
		},

		isSequential(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					parseInt(passcode[i], 10) + 1 ===
						parseInt(passcode[i + 1], 10) &&
					parseInt(passcode[i + 1], 10) + 1 ===
						parseInt(passcode[i + 2], 10)
				) {
					return true
				}
			}
			return false
		},

		isRepeating(passcode) {
			for (let i = 0; i < passcode.length - 2; i++) {
				if (
					passcode[i] === passcode[i + 1] &&
					passcode[i + 2] === passcode[i]
				) {
					return true
				}
			}
			return false
		},

		validatePasscode() {
			const passcode = this.form.passcode

			if (!passcode) {
				this.passcodeErrorMessage = ''
				return true
			}

			if (!/^\d+$/.test(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeDigitsOnly
				return false
			}

			if (passcode.length < 6) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeMinLength
				return false
			}

			if (this.isSequential(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeNoSequential
				return false
			}

			if (this.isRepeating(passcode)) {
				this.passcodeErrorMessage =
					SETTINGS_VALIDATION.passcodeNoRepeating
				return false
			}

			this.passcodeErrorMessage = ''
			return true
		},

		validateOfficePhoneNumber() {
			const sanitizedPhoneNumber = (
				this.form.office_phone_number || ''
			).replace(/\D/g, '')

			const sanitizedTwilioPhone = (
				this.partner?.twilio_phone_number || ''
			)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.officeNumberErrorMessage =
					SETTINGS_VALIDATION.officePhoneRequired
				return false
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.officeNumberErrorMessage =
					SETTINGS_VALIDATION.phoneTenDigits
				return false
			}

			if (
				sanitizedTwilioPhone &&
				sanitizedPhoneNumber === sanitizedTwilioPhone
			) {
				this.officeNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilio
				return false
			}

			this.officeNumberErrorMessage = ''
			return true
		},

		validateOfficeFaxNumber() {
			const sanitizedFaxNumber = (
				this.form.office_fax_number || ''
			).replace(/\D/g, '')

			if (sanitizedFaxNumber === '') {
				this.officeFaxNumberErrorMessage =
					SETTINGS_VALIDATION.officeFaxRequired
				return false
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedFaxNumber)) {
				this.officeFaxNumberErrorMessage =
					SETTINGS_VALIDATION.faxTenDigits
				return false
			}

			this.officeFaxNumberErrorMessage = ''
			return true
		},

		validateFallbackPhoneNumber() {
			const sanitizedPhoneNumber = this.form.fallback_phone_number.replace(
				/\D/g,
				''
			)

			const sanitizedTwilioPhone = (
				this.partner?.twilio_phone_number || ''
			)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			const sanitizedOfficePhone = this.form.office_phone_number
				.replace(/^\+1/, '')
				.replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.fallbackNumberErrorMessage = ''
				return true
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneTenDigits
				return false
			}

			if (
				sanitizedTwilioPhone &&
				sanitizedPhoneNumber === sanitizedTwilioPhone
			) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilioOrOffice
				return false
			}

			if (sanitizedPhoneNumber === sanitizedOfficePhone) {
				this.fallbackNumberErrorMessage =
					SETTINGS_VALIDATION.phoneSameAsTwilioOrOffice
				return false
			}

			this.fallbackNumberErrorMessage = ''
			return true
		},

		async onCopyWMALink() {
			const response = await util.copyToClipboard(this.form.wma_web_link)
			if (response) {
				this.$alert.success(WMA_WEBLINK_SUCCESS_MESSAGE)
			} else {
				this.$alert.error(WMA_WEBLINK_ERROR_MESSAGE)
			}
		},

		async getPartnerData() {
			if (!this.partner?.id) {
				return
			}
			try {
				await this.getPartner(this.partner.id)
			} catch (e) {
				this.$alert.error('default.error')
			} finally {
				this.isForwardingNumbersRefreshing = false
				this.fillForm()
				this.takeSavedSnapshot()
			}
		},

		getCurrentSnapshot() {
			const normalizedEmails = (this.form.rsm_fallback_email || '')
				.split(',')
				.map(e => e.trim())
				.filter(Boolean)
				.join(', ')

			return JSON.stringify({
				form: {
					...this.form,
					rsm_fallback_email: normalizedEmails,
				},
				fallbackEmailTags: [...this.fallbackEmailTags],
			})
		},

		takeSavedSnapshot() {
			this.lastSavedSnapshot = this.getCurrentSnapshot()
		},

		restoreFromSnapshot() {
			if (!this.lastSavedSnapshot) {
				return
			}
			const snapshot = JSON.parse(this.lastSavedSnapshot)
			this.form = { ...getSettingsFormObject(), ...snapshot.form }
			if (snapshot.fallbackEmailTags) {
				this.form.rsm_fallback_email = snapshot.fallbackEmailTags.join(
					', '
				)
			}
		},

		hasRequiredTextValue(value) {
			return Boolean(String(value || '').trim())
		},

		isValidTenDigitPhoneNumber(value) {
			const sanitizedPhoneNumber = String(value || '').replace(/\D/g, '')

			if (!sanitizedPhoneNumber) {
				return false
			}

			return /^[2-9]\d{9}$/.test(sanitizedPhoneNumber)
		},

		isPartnerFieldValidForCallerIdVerification(field) {
			if (field === 'name') {
				return this.hasRequiredTextValue(this.form.name)
			}

			if (field === 'ob_caller_id_number') {
				return this.isOutboundCallerIdValid
			}

			if (field === 'office_phone_number') {
				if (
					!this.isValidTenDigitPhoneNumber(
						this.form.office_phone_number
					)
				) {
					return false
				}

				const sanitizedPhoneNumber = (
					this.form.office_phone_number || ''
				).replace(/\D/g, '')
				const sanitizedTwilioPhone = (
					this.partner?.twilio_phone_number || ''
				)
					.replace(/^\+1/, '')
					.replace(/\D/g, '')

				if (
					sanitizedTwilioPhone &&
					sanitizedPhoneNumber === sanitizedTwilioPhone
				) {
					return false
				}

				return true
			}

			if (field === 'office_fax_number') {
				return this.isValidTenDigitPhoneNumber(
					this.form.office_fax_number
				)
			}

			if (field === 'wma_web_link') {
				return this.hasRequiredTextValue(this.form.wma_web_link)
			}

			if (
				[
					'address_1',
					'city',
					'state_province',
					'postal_code',
					'country',
				].includes(field)
			) {
				return this.hasRequiredTextValue(this.form[field])
			}

			return true
		},

		hasCallerIdVerificationFieldError(field) {
			if (!this.lastSavedSnapshot) {
				return false
			}

			return (
				this.showCallerIdVerificationRequiredErrors &&
				!this.isPartnerFieldValidForCallerIdVerification(field)
			)
		},

		clearOutboundCallerIdNumber() {
			this.form.ob_caller_id_number = ''
			this.outboundCallerIdErrorMessage = ''
		},

		syncOutboundCallerIdFromPartner() {
			const value = this.partner?.ob_caller_id_number

			if (!value) {
				this.form.ob_caller_id_number = ''
				return
			}

			this.form.ob_caller_id_number = String(value)
				.replace(/^\+1/, '')
				.replace(/\D/g, '')
		},

		validateOutboundCallerIdNumber() {
			const sanitizedPhoneNumber = (
				this.form.ob_caller_id_number || ''
			).replace(/\D/g, '')

			if (sanitizedPhoneNumber === '') {
				this.outboundCallerIdErrorMessage =
					SETTINGS_VALIDATION.outboundCallerIdRequired
				return false
			}

			if (!/^[2-9]\d{9}$/.test(sanitizedPhoneNumber)) {
				this.outboundCallerIdErrorMessage =
					SETTINGS_VALIDATION.phoneTenDigits
				return false
			}

			this.outboundCallerIdErrorMessage = ''
			return true
		},

		openVerifyCallerIdPanel() {
			if (this.isVerifyCallerIdDisabled) {
				return
			}

			if (!this.validateOutboundCallerIdNumber()) {
				return
			}

			this.showVerifyCallerIdPanel = true
		},

		closeVerifyCallerIdPanel() {
			this.showVerifyCallerIdPanel = false
		},

		onVerifyPanelPhoneNumberChange(phoneNumber) {
			this.form.ob_caller_id_number = phoneNumber
		},

		onOutboundCallerIdVerified(phoneNumber) {
			this.form.ob_caller_id_number = phoneNumber
		},

		clearGeneralInfoField(field) {
			if (field === 'office_phone_number') {
				this.rawOfficePhoneNumber = ''
				this.validateOfficePhoneNumber()
				return
			}

			if (field === 'office_fax_number') {
				this.rawOfficeFaxNumber = ''
				this.validateOfficeFaxNumber()
				return
			}

			if (field === 'fallback_phone_number') {
				this.rawFallbackPhoneNumber = ''
				return
			}

			if (Object.prototype.hasOwnProperty.call(this.form, field)) {
				this.form[field] = ''
			}
		},

		startPanelEditing(panel) {
			GENERAL_INFO_PANEL_IDS.forEach(otherPanel => {
				if (otherPanel !== panel && this.panelEditing[otherPanel]) {
					this.cancelPanelEditing(otherPanel)
				}
			})
			this.$set(this.panelEditing, panel, true)
		},

		cancelPanelEditing(panel) {
			this.restorePanelFromSnapshot(panel)
			this.$set(this.panelEditing, panel, false)
			if (panel === 'practice') {
				this.selectedForwardingNumber = null
				this.outboundCallerIdErrorMessage = ''
			}
		},

		async onPanelSave(panel) {
			if (this.isPanelSaveDisabled(panel)) {
				return
			}

			if (
				panel === 'practice' &&
				!this.validateOutboundCallerIdNumber()
			) {
				this.$alert.error(this.outboundCallerIdErrorMessage)
				return
			}

			// if (panel === 'practice' && !this.validateOfficePhoneNumber()) {
			// 	return
			// }

			// if (panel === 'practice' && !this.validateOfficeFaxNumber()) {
			// 	return
			// }

			const saved = await this.savePartnerSettings()
			if (saved) {
				this.$set(this.panelEditing, panel, false)
				if (panel === 'practice') {
					this.selectedForwardingNumber = null
				}
				await this.refreshSettingsPartner()
			}
		},

		isPanelSaveDisabled(panel) {
			if (!this.partner || !this.lastSavedSnapshot) {
				return true
			}

			return (
				this.getPanelSnapshot(panel) ===
				this.getPanelSnapshotFromSaved(panel)
			)
		},

		getPanelSnapshot(panel) {
			const fields = GENERAL_INFO_PANEL_FIELDS[panel] || []
			const slice = {}

			fields.forEach(field => {
				slice[field] = this.form[field] ?? null
			})

			return JSON.stringify(slice)
		},

		getPanelSnapshotFromSaved(panel) {
			if (!this.lastSavedSnapshot) {
				return ''
			}

			const saved = JSON.parse(this.lastSavedSnapshot)
			const fields = GENERAL_INFO_PANEL_FIELDS[panel] || []
			const slice = {}

			fields.forEach(field => {
				slice[field] = saved.form?.[field] ?? null
			})

			return JSON.stringify(slice)
		},

		restorePanelFromSnapshot(panel) {
			if (!this.lastSavedSnapshot) {
				return
			}

			const saved = JSON.parse(this.lastSavedSnapshot)
			const fields = GENERAL_INFO_PANEL_FIELDS[panel] || []

			fields.forEach(field => {
				if (saved.form && field in saved.form) {
					this.form[field] = saved.form[field]
				}
			})
		},

		mergePanelIntoSnapshot(panel) {
			if (!this.lastSavedSnapshot) {
				this.takeSavedSnapshot()
				return
			}

			const snapshot = JSON.parse(this.lastSavedSnapshot)
			const fields = GENERAL_INFO_PANEL_FIELDS[panel] || []

			fields.forEach(field => {
				snapshot.form[field] = this.form[field]
			})

			this.lastSavedSnapshot = JSON.stringify(snapshot)
		},

		onManageOrganization() {
			if (this.selectSettingsSection) {
				this.selectSettingsSection('organization')
			}
		},

		resetGeneralInfoForPartnerChange() {
			this.panelEditing = {
				practice: false,
				address: false,
				fallback: false,
			}
			this.selectedForwardingNumber = null
			this.outboundCallerIdErrorMessage = ''
			this.showVerifyCallerIdPanel = false
		},

		cancelAllEditing() {
			GENERAL_INFO_PANEL_IDS.forEach(panel => {
				if (this.panelEditing[panel]) {
					this.cancelPanelEditing(panel)
				}
			})
		},

		...mapActions({
			update: 'partners/update',
			getPartner: 'partners/find',
			setForwardingNumber: 'partners/setForwardingNumber',
		}),
	},

	name: 'GeneralInformationSection',

	props: {
		organizationName: {
			type: String,
			default: '',
		},
	},

	watch: {
		'form.passcode'(newVal) {
			if (newVal.length === 0) {
				this.wasCleared = true
			}
		},

		partner: {
			immediate: true,
			handler(newPartner, oldPartner) {
				if (!newPartner) {
					return
				}
				if (!oldPartner || newPartner.id !== oldPartner.id) {
					this.fillForm()
					if (!this.ensureSettingsPartnerLoaded) {
						this.$nextTick(() => {
							if (!this.lastSavedSnapshot) {
								this.takeSavedSnapshot()
							}
						})
					}
				}
			},
		},

		async 'partner.id'(newId, oldId) {
			if (!newId || newId === oldId || oldId == null) {
				return
			}

			this.resetGeneralInfoForPartnerChange()
			await this.loadPartnerForm({ force: true })
		},

		'partner.ob_caller_id_number': {
			immediate: true,
			handler() {
				if (!this.panelEditing.practice) {
					this.syncOutboundCallerIdFromPartner()
				}
			},
		},
	},

	async created() {
		this.form = getSettingsFormObject()
		this.timezones = moment.tz.zonesForCountry('us')

		if (this.deferPartnerFormInit) {
			return
		}

		await this.initializeSettingsPartnerForm()
	},

	beforeDestroy() {
		this.cancelAllEditing()
	},

	deactivated() {
		this.cancelAllEditing()
	},

	data() {
		return {
			common: SETTINGS_COMMON,
			strings: GENERAL_INFORMATION_STRINGS,
			placeholders: SETTINGS_PLACEHOLDERS,
			aria: SETTINGS_ARIA,
			form: getSettingsFormObject(),
			isPartnerFormLoading: false,
			timezones: [],
			wasCleared: false,
			lastSavedSnapshot: null,
			showPasscode: false,
			passcodeErrorMessage: '',
			fallbackNumberErrorMessage: '',
			officeNumberErrorMessage: '',
			officeFaxNumberErrorMessage: '',
			emailErrorMessage: '',
			isForwardingNumbersRefreshing: false,
			selectedForwardingNumber: null,
			showGeneralPopup: false,
			panelEditing: {
				practice: false,
				address: false,
				fallback: false,
			},
			positiveLabel: 'Confirm',
			negativeLabel: 'Cancel',
			positiveFirst: false,
			outboundCallerIdErrorMessage: '',
			showVerifyCallerIdPanel: false,
		}
	},
}
</script>
